import {
    Multiselect,
    MultiselectProps,
    Select,
    SelectProps
} from '@cloudscape-design/components';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';
// Note: Assuming OptionDefinition type is available globally if needed,
// but for ItemGroupType, we define the full structure.


// --- CORE TYPES ---
type ItemGroupType = {
    idGroupType: number;
    groupType: string;
    bookingDeleteTime: number;
    bookAheadTime: number;
    bookingMaxGroup: number;
    maxBookingTime: number;
    minBookHour: number;
    maxBookHour: number;
    idBookingTypes: any[]; // Defined as any[] based on user's structure
    showOnBooking: number;
    showOnFlightLog: number;
    hasPax: number;
    jsonConfigEquipment: any; // Defined as any for unknown config
    groupTypeMaster: string;
    groupTypeMasterCode: string;
    value: string; // Cloudscape value (idGroupType.toString())
    label: string; // Cloudscape label (groupType)
};

// --- PROPS INTERFACES ---

// Base props common to both modes
interface SelectGroupTypeBaseProps {
    visible?: boolean;
    showZero?: boolean;
    width?: number;
    showOnBooking?: number; // Used for filtering the list
    showOnFlightLog?: number; // Used for filtering the list
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
    idGroupTypes?: number[];
}

// 1. When single = true
interface SelectGroupTypeSingleProps extends SelectGroupTypeBaseProps {
    single: true;
    value?: number; // Single ID
    width?: number;
    onSelect?: (selected: number | null) => void; // Returns ID or null
}

// 2. When single = false (or undefined)
interface SelectGroupTypeMultiProps extends SelectGroupTypeBaseProps {
    single?: false; // default is "false"
    value?: number[]; // Array of IDs
    width?: number;
    onSelect?: (selected: number[]) => void; // Returns array of IDs
}

export type SelectGroupTypeProps =
    | SelectGroupTypeSingleProps
    | SelectGroupTypeMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectGroupType: React.FC<SelectGroupTypeProps> = (props) => {
    const {
        single,
        width = 200,
        value,
        readonly,
        visible,
        tokenLimit = 3,
        hideTokens = false,
        showZero = false,
        showOnBooking = 0,
        showOnFlightLog = 0,
        idGroupTypes = [],
        onSelect
    } = props;

    const { data } = useGlobalState();
    if (!data || !data.groupTypes) return <></>;

    // State for Single Selection (holds the selected object)
    const [selectedSingle, setSelectedSingle] = useState<ItemGroupType | null>(null);

    // State for Multi Selection (holds the array of selected objects)
    const [selectedMulti, setSelectedMulti] = useState<ItemGroupType[]>([]);


    // --- Mapped Group Types (Options List) ---

    // Memoize the calculation of available options
    const mappedGroupTypes: ItemGroupType[] = useMemo(() => {
        const idGroupTypesSet = new Set(idGroupTypes);
        let groupTypes: ItemGroupType[] = data.groupTypes
            .filter((plan: ItemGroupType) => !showOnBooking || plan.showOnBooking === 1)
            .filter((plan: ItemGroupType) =>
                idGroupTypesSet.size === 0 ||
                idGroupTypesSet.has(plan.idGroupType)
            )
            .map((plan: ItemGroupType) => ({
                ...plan,
                value: plan.idGroupType.toString(),
                label: plan.groupType
            }));

        if (showZero) {
            groupTypes.unshift({
                idGroupType: 0,
                groupType: "No asignado",
                value: "0",
                label: "No asignado",
                bookingDeleteTime: 0,
                bookAheadTime: 0,
                bookingMaxGroup: 0,
                maxBookingTime: 0,
                minBookHour: 0,
                hasPax: 0,
                idBookingTypes: [],
                maxBookHour: 0,
                showOnBooking: 0,
                showOnFlightLog: 0,
                jsonConfigEquipment: undefined,
                groupTypeMaster: '',
                groupTypeMasterCode: ''
            });
        }
        return groupTypes;
    }, [data.groupTypes, showOnBooking, showZero]);


    // --- Initialization & Value Sync Effect ---

    useEffect(() => {
        if (!value) {
            setSelectedSingle(null);
            setSelectedMulti([]);
            return;
        }

        if (single) {
            // SINGLE MODE: Value is a number (ID)
            const id = value as number;
            const match = mappedGroupTypes.find(gt => gt.idGroupType === id) || null;
            setSelectedSingle(match);

        } else {
            // MULTI MODE: Value is an array of numbers (IDs)
            const ids = value as number[];
            const matches = mappedGroupTypes.filter(gt => ids.includes(gt.idGroupType));
            setSelectedMulti(matches);
        }
    }, [value, single, mappedGroupTypes]);


    // --- Single Select Handler ---
    const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
        const selectedOption = detail.selectedOption as ItemGroupType | null;

        setSelectedSingle(selectedOption);

        if (onSelect && single) {
            const selectedId = selectedOption?.idGroupType ?? null; // Extract ID
            // Assert the function signature to number | null
            (onSelect as (selected: number | null) => void)(selectedId);
        }
    };

    // --- Multi Select Handler ---
    const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
        const list = detail.selectedOptions as ItemGroupType[];

        // Use spread to ensure setting a new array reference
        setSelectedMulti([...list]);

        if (onSelect && !single) {
            const selectedIds = list.map(item => item.idGroupType); // Extract array of IDs
            // Assert the function signature to number[]
            (onSelect as (selected: number[]) => void)(selectedIds);
        }
    };

    // --- Rendering ---

    //const placeholder = single ? "Seleccionar grupo" : "Seleccionar grupos";


    const placeholder = single ? "Seleccionar grupo" : selectedMulti && selectedMulti.length > 0 ? `${selectedMulti.length} seleccionado${selectedMulti.length > 1 ? 's' : ''}` : "Seleccionar grupos";

    const wrapperStyle: React.CSSProperties = {
        width: width > 0 ? `${width}px` : '100%'
    };
    return (
        <div style={wrapperStyle}>
            {single ? (
                // SINGLE SELECTION
                <Select
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOption={selectedSingle}
                    options={mappedGroupTypes}
                    onChange={handleSingleChange}
                />
            ) : (
                // MULTI SELECTION
                <Multiselect
                    tokenLimit={tokenLimit}
                    hideTokens={hideTokens}
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOptions={selectedMulti}
                    options={mappedGroupTypes}
                    onChange={handleMultiChange}
                />
            )}
        </div>
    );
};

export default SelectGroupType;